import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import JsBarcode from "jsbarcode";

/** ข้อมูลสำหรับ label — ใช้เมื่อสร้าง PDF (ส่งเองใช้ factoryName + factoryCode + productSku) */
export interface ShippingLabelData {
  /** ชื่อโรงงาน (ใช้เมื่อส่งเอง) */
  factoryName?: string;
  /** รหัสโรงงาน (ใช้เมื่อส่งเอง) — แสดงต่อท้าย factory_name */
  factoryCode?: string;
  /** เบอร์โทรผู้ประกอบการ — แสดงเป็นอีกบรรทัดต่อจาก order_code */
  factoryPhone?: string;
  orderCode: string;
  /** เลขพัสดุ — ใช้เข้ารหัสใน Barcode (CODE128) ด้านบนของ label */
  trackingNumber?: string;
  /** ชื่อสินค้า */
  productName: string;
  /** SKU สินค้า (ใช้เมื่อส่งเอง) */
  productSku?: string;
  /** ชื่อผู้รับ */
  recipientName?: string;
  /** เบอร์โทรผู้รับ */
  recipientPhone?: string;
  /** ที่อยู่ผู้รับ บรรทัดที่ 1 (บ้านเลขที่ หมู่ ซอย ถนน) */
  addressLine1?: string;
  /** ที่อยู่ผู้รับ บรรทัดที่ 2 (ตำบล + อำเภอ รวมบรรทัดเดียวกัน) */
  addressLine2?: string;
  /** ที่อยู่ผู้รับ บรรทัดที่ 3 (ปัจจุบันไม่มี caller ไหนส่งมาแล้ว — ตำบล/อำเภอ ถูกรวมไว้ที่ addressLine2 แทน) */
  addressLine3?: string;
  /** ที่อยู่ผู้รับ บรรทัดที่ 4 (จังหวัด รหัสไปรษณีย์) */
  addressLine4?: string;
}

/** path ฟอนต์ Sarabun (อยู่ที่ public/assets/fonts/Sarabun-Regular.ttf) */
const SARABUN_FONT_URL = `/assets/fonts/Sarabun-Regular.ttf`;

/** path โลโก้ Bangbow (อยู่ที่ public/logoBangbow.png) */
const LOGO_BANGBOW_URL = `/logoBangbow.png`;

/** แปลง Uint8Array เป็น base64 */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

const SARABUN_FONT_FACE_NAME = "SarabunPDF";

/** ข้อความ "ชื่อสินค้า" แบบระบุ Unicode ชัดเจน (ช+ื+ไม้เอก ่+อ สินค้า) เพื่อให้ไม้เอกไม่หาย */
const LABEL_PRODUCT_NAME = "\u0E0A\u0E36\u0E48\u0E2D\u0E2A\u0E34\u0E19\u0E04\u0E49\u0E32";

/** โหลดฟอนต์ Sarabun ใน document (สำหรับ Canvas) — เรียกก่อน render ข้อความไทย */
async function ensureSarabunFontLoaded(): Promise<void> {
  if (typeof document === "undefined") return;
  const hasSarabun = Array.from(document.fonts.values()).some(
    (f) => f.family === SARABUN_FONT_FACE_NAME
  );
  if (hasSarabun) return;
  const fontUrl = `${window.location.origin}${SARABUN_FONT_URL}`;
  const fontFace = new FontFace(SARABUN_FONT_FACE_NAME, `url(${fontUrl})`);
  const loaded = await fontFace.load();
  document.fonts.add(loaded);
}

/**
 * เรนเดอร์ข้อความ (โดยเฉพาะไทย เช่น "ชื่อสินค้า") ด้วย Canvas ของเบราว์เซอร์ แล้ว return เป็น PNG bytes
 * ใช้แก้ปัญหา pdf-lib แสดงตัวสะกด/สระไทยผิดที่
 */
async function renderTextToPngBytes(
  text: string,
  fontSizePt: number
): Promise<Uint8Array> {
  await ensureSarabunFontLoaded();
  const scale = 3;
  const fontSizePx = Math.round(fontSizePt * scale);
  const paddingH = 6;
  // paddingTop เดิม 10 ไม่พอ — สระ+วรรณยุกต์ซ้อนกันของภาษาไทย (เช่น "นี้" = ี ซ้อน ้) ให้ ink สูงเกิน
  // em-box top ที่ font metric รายงาน (ชนขอบบนของ canvas ที่ตำแหน่ง y=0 แล้วโดนตัด เพราะ
  // textBaseline="top" อ้างอิง em-box ไม่ใช่ ink จริง) เพิ่ม padding ให้มีที่เผื่อเกินขอบ
  const paddingTop = 20;
  const paddingBottom = 6;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2d not available");
  const normalizedText = text.normalize("NFC");
  ctx.font = `${fontSizePx}px ${SARABUN_FONT_FACE_NAME}, sans-serif`;
  const metrics = ctx.measureText(normalizedText);
  const w = Math.ceil(metrics.width) + paddingH * 2;
  const h = paddingTop + fontSizePx + paddingBottom;
  canvas.width = w;
  canvas.height = h;
  ctx.font = `${fontSizePx}px ${SARABUN_FONT_FACE_NAME}, sans-serif`;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = "#000000";
  ctx.textBaseline = "top";
  ctx.fillText(normalizedText, paddingH, paddingTop);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas toBlob failed"));
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          const arr = new Uint8Array(reader.result as ArrayBuffer);
          resolve(arr);
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
      },
      "image/png",
      1
    );
  });
}

/** วัดความกว้างของข้อความ (แปลงเป็นหน่วย PDF point ที่ lineHeightPt ตามจริง) ด้วย Canvas เดียวกับ
 * renderTextToPngBytes — ค่า scale/padding ต้องตรงกับฟังก์ชันนั้นเป๊ะ เพื่อให้ตัดบรรทัดได้พอดีกับ
 * ที่ embedSimpleText จะ render ออกมาจริง */
async function measureTextWidthPt(
  text: string,
  fontSizePt: number,
  lineHeightPt: number
): Promise<number> {
  await ensureSarabunFontLoaded();
  const scale = 3;
  const fontSizePx = Math.round(fontSizePt * scale);
  const paddingTop = 20; // ต้องตรงกับ renderTextToPngBytes เป๊ะ (ดูคอมเมนต์ที่นั่น)
  const paddingBottom = 6;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2d not available");
  ctx.font = `${fontSizePx}px ${SARABUN_FONT_FACE_NAME}, sans-serif`;
  const widthPx = ctx.measureText(text.normalize("NFC")).width;
  const pixelHeight = paddingTop + fontSizePx + paddingBottom;
  return widthPx * (lineHeightPt / pixelHeight);
}

/**
 * ตัดข้อความขึ้นบรรทัดใหม่ไม่เกิน maxLines บรรทัด ให้แต่ละบรรทัดกว้างไม่เกิน maxWidthPt —
 * ตัดทีละตัวอักษร (ไม่ใช่ word-wrap แบบอังกฤษ) เพราะภาษาไทยไม่มีช่องว่างระหว่างคำ ถ้าเนื้อหายังเหลือ
 * หลังบรรทัดสุดท้าย ตัดสั้นลงอีกแล้วใส่ "..." ต่อท้าย
 */
async function wrapTextToLines(
  text: string,
  maxWidthPt: number,
  maxLines: number,
  fontSizePt: number,
  lineHeightPt: number
): Promise<string[]> {
  const lines: string[] = [];
  let remaining = text.trim();

  while (remaining.length > 0 && lines.length < maxLines) {
    const isLastAllowedLine = lines.length === maxLines - 1;

    let fit = remaining;
    while (fit.length > 1 && (await measureTextWidthPt(fit, fontSizePt, lineHeightPt)) > maxWidthPt) {
      fit = fit.slice(0, -1);
    }

    if (isLastAllowedLine && fit.length < remaining.length) {
      let truncated = fit;
      while (
        truncated.length > 1 &&
        (await measureTextWidthPt(`${truncated}...`, fontSizePt, lineHeightPt)) > maxWidthPt
      ) {
        truncated = truncated.slice(0, -1);
      }
      lines.push(`${truncated}...`);
      remaining = "";
    } else {
      lines.push(fit);
      remaining = remaining.slice(fit.length);
    }
  }

  return lines;
}

/**
 * เรนเดอร์ Barcode (CODE128) ด้วย JsBarcode ลง Canvas แล้ว return เป็น PNG bytes — ใช้ข้อมูลเดียวกับ
 * ที่เคยเข้ารหัสใน QR เดิม (trackingNumber) รองรับตัวอักษร+ตัวเลขผสมกันได้ (เช่น "BB1696489200000")
 */
async function renderBarcodeToPngBytes(
  value: string,
  heightPx: number,
  barWidthPx: number
): Promise<Uint8Array> {
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, value, {
    format: "CODE128",
    width: barWidthPx,
    height: heightPx,
    displayValue: false,
    margin: 0,
  });
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas toBlob failed"));
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          const arr = new Uint8Array(reader.result as ArrayBuffer);
          resolve(arr);
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
      },
      "image/png",
      1
    );
  });
}

/**
 * สร้าง PDF Label ขนาด 5x4 นิ้ว แล้ว return เป็น base64
 * ใช้ฟอนต์ Sarabun (Canvas) รองรับภาษาไทย, โลโก้ + Bangbow Platform, Barcode ด้านบน + ที่อยู่ด้านล่าง
 * หน้าอื่นสามารถ import เรียกใช้กับข้อมูล label ได้โดยตรง
 */
export async function createShippingLabelPdfBase64(
  labelData: ShippingLabelData
): Promise<string> {
  const doc = await PDFDocument.create();
  const titleFont = await doc.embedStandardFont(StandardFonts.HelveticaBold);

  // โหลดโลโก้ Bangbow
  const logoRes = await fetch(LOGO_BANGBOW_URL);
  if (!logoRes.ok) {
    throw new Error(`โหลดโลโก้ไม่สำเร็จ: ${LOGO_BANGBOW_URL} (วางไฟล์ที่ public/logoBangbow.png)`);
  }
  const logoBytes = new Uint8Array(await logoRes.arrayBuffer());
  const logoImage = await doc.embedPng(logoBytes);

  // Barcode (CODE128) — ใช้แค่ trackingNumber อย่างเดียว ไม่รวม order_code (ข้อมูลเดียวกับ QR เดิม)
  const barcodeImage = labelData.trackingNumber
    ? await doc.embedPng(await renderBarcodeToPngBytes(labelData.trackingNumber, 240, 3))
    : null;

  // บรรทัดซ้าย: Factory (ถ้ามี), เบอร์โทรผู้ประกอบการ (ถ้ามี), Order (ชิดซ้าย) + เลขที่อ้างอิง
  // (ชิดขวา) บรรทัดเดียวกัน, ชื่อสินค้า, SKU (แยกคนละบรรทัด) — ใช้ Canvas เรนเดอร์ทั้งหมด
  const FIELD_FONT_SIZE = 11;
  const FIELD_LINE_HEIGHT = 14;
  const FIELD_MAX_LINES = 2;
  // ต้องตรงกับ pageWidth/margin/pad ที่ประกาศด้านล่าง (ความกว้างพื้นที่เนื้อหาจริงในหน้า label)
  const FIELD_MAX_WIDTH_PT = 5 * 72 - 2 * 8 - 2 * 12;

  const linePhoneText = labelData.factoryPhone != null && labelData.factoryPhone !== ""
    ? `โทร : ${labelData.factoryPhone}`
    : "";
  const orderText = `Order : ${labelData.orderCode}`;

  const rows: Awaited<ReturnType<PDFDocument["embedPng"]>>[] = [];

  const embedSimpleText = async (text: string) => doc.embedPng(await renderTextToPngBytes(text, FIELD_FONT_SIZE));

  /** ตัดขึ้นบรรทัดใหม่ไม่เกิน FIELD_MAX_LINES บรรทัด แล้ว push แต่ละบรรทัดเป็น row แยกกัน */
  const pushWrappedField = async (text: string) => {
    const lines = await wrapTextToLines(text, FIELD_MAX_WIDTH_PT, FIELD_MAX_LINES, FIELD_FONT_SIZE, FIELD_LINE_HEIGHT);
    for (const line of lines) {
      rows.push(await embedSimpleText(line));
    }
  };

  if (labelData.factoryName) await pushWrappedField(labelData.factoryName);
  if (linePhoneText) rows.push(await embedSimpleText(linePhoneText));
  rows.push(await embedSimpleText(orderText));
  await pushWrappedField(labelData.productName);
  if (labelData.productSku) await pushWrappedField(labelData.productSku);

  // --- Address Texts Rendering ---
  const addressTexts: string[] = [];
  if (labelData.recipientName) {
    addressTexts.push(`ผู้รับ : ${labelData.recipientName} ${labelData.recipientPhone || ""}`);
  }
  if (labelData.addressLine1) addressTexts.push(labelData.addressLine1);
  if (labelData.addressLine2) addressTexts.push(labelData.addressLine2);
  if (labelData.addressLine3) addressTexts.push(labelData.addressLine3);
  if (labelData.addressLine4) addressTexts.push(labelData.addressLine4);

  const addressPngBytesArr = await Promise.all(
    addressTexts.map((text) => renderTextToPngBytes(text, 11))
  );
  const addressImages = await Promise.all(
    addressPngBytesArr.map((bytes) => doc.embedPng(bytes))
  );

  const page = doc.addPage([5 * 72, 4 * 72]); // 5in x 4in (72 pt per inch)
  const margin = 8;
  const pageWidth = 5 * 72;
  const pageHeight = 4 * 72;
  const borderWidth = 1.5;
  const borderColor = rgb(0.25, 0.25, 0.25);
  const lineColor = rgb(0.4, 0.4, 0.4);

  // --- พื้นที่ label (ไม่วาดกรอบนอกแล้ว — มีกรอบเฉพาะส่วนที่อยู่ผู้รับด้านล่าง) ---
  const frameX = margin;
  const frameY = margin;
  const frameW = pageWidth - 2 * margin;
  const frameH = pageHeight - 2 * margin;

  // พื้นที่วาดเนื้อหา (เว้นจากขอบหน้าเท่าเดิมตอนที่ยังมีกรอบนอก)
  const pad = 12;
  const innerLeft = frameX + pad;
  const innerRight = frameX + frameW - pad;
  const innerTop = frameY + frameH - pad;
  const headerUpShift = 8; // เพิ่มจาก 4 เพื่อเลื่อนบล็อกบนสุด (คำเตือน/Barcode/โลโก้) ขึ้นอีกนิด
  let y = innerTop + headerUpShift;

  // --- ลำดับบนสุด: 1) คำเตือน "โปรดอย่าปิดทับส่วนนี้" 2) Barcode (ใหญ่เต็มความกว้าง)
  // 3) โลโก้ + "Bangbow Platform" (ซ้าย) + เลข BB (ขวา) บรรทัดเดียวกัน 4) เส้นคั่น ---
  const maxW = innerRight - innerLeft;

  // 1) คำเตือนไม่ให้ปิดทับส่วนนี้ — บรรทัดบนสุด กึ่งกลาง
  // ขนาด 13 ปลอดภัยแล้วหลังเพิ่ม paddingTop เป็น 20 ใน renderTextToPngBytes/measureTextWidthPt
  // (ไม้โทที่ "นี้" เคยโดนตัดตอนฟอนต์ใหญ่ เพราะ padding เดิมไม่พอ ไม่ใช่เพราะขนาดฟอนต์)
  const warningImage = await doc.embedPng(await renderTextToPngBytes("โปรดอย่าปิดทับ barcode นี้", 13));
  const warningH = 17;
  const warningW = (warningImage.width / warningImage.height) * warningH;
  page.drawImage(warningImage, {
    x: innerLeft + (maxW - warningW) / 2,
    y: y - warningH,
    width: warningW,
    height: warningH,
  });
  y -= warningH + 3;

  // 2) Barcode — เต็มความกว้างเสมอ (ไม่ยึดสัดส่วนธรรมชาติ)
  if (barcodeImage) {
    const barcodeDrawH = 48 * 0.9 * 0.95 * 0.95; // ลดความสูงลง 10% จาก 48 แล้วลดอีก 5% + 5%
    const barcodeDrawW = maxW;
    page.drawImage(barcodeImage, {
      x: innerLeft,
      y: y - barcodeDrawH,
      width: barcodeDrawW,
      height: barcodeDrawH,
    });
    y -= barcodeDrawH + 6;
  }

  // 3) โลโก้ + "Bangbow Platform" (ซ้าย) + เลข BB (ขวา) บรรทัดเดียวกัน — ตัวเล็ก ไม่เด่นเหมือนก่อน
  const headerHeight = 20;
  const logoHeight = 16;
  const logoScale = logoHeight / logoImage.height;
  const logoWidth = logoImage.width * logoScale;
  page.drawImage(logoImage, {
    x: innerLeft,
    y: y - logoHeight,
    width: logoWidth,
    height: logoHeight,
  });
  const titleText = "Bangbow Platform";
  const titleSize = 11;
  page.drawText(titleText, {
    x: innerLeft + logoWidth + 8,
    y: y - logoHeight + (logoHeight - titleSize) / 2,
    size: titleSize,
    font: titleFont,
    color: rgb(0, 0, 0),
  });
  if (labelData.trackingNumber) {
    const refFontSize = 14; // ใหญ่กว่า titleSize ให้เลขพัสดุเด่นกว่าชื่อแพลตฟอร์ม
    const refWidth = titleFont.widthOfTextAtSize(labelData.trackingNumber, refFontSize);
    page.drawText(labelData.trackingNumber, {
      x: innerRight - refWidth,
      y: y - logoHeight + (logoHeight - refFontSize) / 2,
      size: refFontSize,
      font: titleFont,
      color: rgb(0, 0, 0),
    });
  }
  y -= headerHeight;

  // 4) กรอบครอบส่วนผู้ส่ง + ผู้รับ — ขอบบนแทนเส้นคั่นใต้ barcode เดิม กว้างเท่า barcode
  // ยาวลงถึงขอบล่างของ label (ส่วน barcode ด้านบนไม่มีกรอบ)
  const boxPad = 6;
  page.drawRectangle({
    x: innerLeft,
    y: frameY,
    width: innerRight - innerLeft,
    height: y - frameY,
    borderColor,
    borderWidth,
  });
  y -= 6;

  // --- โซนรายละเอียด (ตาราง - แถวข้อมูล) ---
  const leftColX = innerLeft + boxPad;
  const lineHeight = 14;
  const drawLineImage = (
    img: Awaited<ReturnType<PDFDocument["embedPng"]>>,
    x: number,
    yPos: number,
    lineH: number
  ) => {
    const w = (img.width / img.height) * lineH;
    page.drawImage(img, { x, y: yPos - lineH, width: w, height: lineH });
  };
  for (let i = 0; i < rows.length; i++) {
    drawLineImage(rows[i], leftColX, y, lineHeight);
    y -= lineHeight;
    // ไม่วาดเส้นคั่นระหว่างแถว (ชื่อร้าน / เบอร์โทร / order_code+เลขที่อ้างอิง / ชื่อสินค้า / SKU)
    // — เว้นระยะเปล่าแทน
    if (i < rows.length - 1) {
      y -= 2;
    }
  }

  y -= 20; // เพิ่มจาก 14 เลื่อนทั้งเส้นคั่นและที่อยู่ผู้รับ (ตามมาด้วย) ลงมาอีกนิด
  // เส้นคั่นก่อนที่อยู่ผู้รับ (แบ่งส่วนผู้ส่งกับส่วนผู้รับ ภายในกรอบเดียวกัน)
  page.drawLine({
    start: { x: innerLeft, y },
    end: { x: innerRight, y },
    thickness: borderWidth,
    color: lineColor,
  });
  y -= 4;

  // --- วาดที่อยู่ผู้รับในกรอบ (เต็มความกว้าง — barcode ย้ายขึ้นไปบนสุดแล้ว ไม่มีอะไรแชร์พื้นที่
  // ฝั่งขวาอีก) ไหลต่อจาก y ของแถวข้อมูลด้านบนตรงๆ ไม่ต้อง anchor จากขอบล่างอีกต่อไป ---
  const addressX = innerLeft + boxPad;
  const addressLineHeight = 14;
  const addressLineGap = 2;
  const addressDownShift = 4; // ลดลงจาก 16 ให้ "ผู้รับ" ขยับขึ้นมาใกล้เส้นคั่นมากขึ้น
  let addressY = y - addressDownShift;

  for (let i = 0; i < addressImages.length; i++) {
    const img = addressImages[i];
    const w = (img.width / img.height) * addressLineHeight;
    page.drawImage(img, { x: addressX, y: addressY - addressLineHeight, width: w, height: addressLineHeight });
    addressY -= (addressLineHeight + addressLineGap);
  }

  const pdfBytes = await doc.save();
  return uint8ArrayToBase64(new Uint8Array(pdfBytes));
}
